import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Code2, MessageSquareText, PenTool, Rocket } from "lucide-react";
import { SectionHeading, Reveal } from "./Reveal";
import { CTAButton } from "./CTAButton";
import { EASE } from "@/lib/site";
import { goToContact } from "@/lib/scroll";

const STEPS = [
  { icon: MessageSquareText, title: "Você conta sua ideia", text: "Entendemos seu negócio, seus objetivos e o que você precisa." },
  { icon: PenTool, title: "Planejamos o projeto", text: "Definimos a estrutura, experiência, design e funcionalidades." },
  { icon: Code2, title: "Desenvolvemos", text: "Transformamos o projeto em uma solução digital funcional e profissional." },
  { icon: Rocket, title: "Entregamos", text: "Colocamos seu projeto em funcionamento e entregamos tudo pronto para você utilizar." },
];

const Step = ({ s, i }) => (
  <motion.li
    data-testid={`process-step-${i + 1}`}
    initial={{ opacity: 0, x: 40 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.8, ease: EASE }}
    className="group relative pb-14 pl-20 last:pb-0 sm:pl-24"
  >
    <motion.span
      initial={{ scale: 0.6, opacity: 0.4 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="absolute left-0 top-0 grid h-14 w-14 place-items-center rounded-2xl border border-ws-lime/40 bg-ws-bg text-ws-lime shadow-[0_0_30px_-6px_rgba(124,255,26,.6)] transition-colors duration-300 group-hover:bg-ws-lime group-hover:text-ws-bg sm:h-16 sm:w-16"
    >
      <s.icon size={24} />
    </motion.span>
    <div className="rounded-3xl border border-white/[0.07] bg-ws-card p-6 transition-[border-color] duration-300 group-hover:border-ws-lime/30 sm:p-8">
      <p className="font-mono text-xs text-ws-lime">ETAPA 0{i + 1}</p>
      <h3 className="mt-3 text-xl font-medium tracking-tight sm:text-2xl">{s.title}</h3>
      <p className="mt-3 text-[15px] leading-relaxed text-ws-muted">{s.text}</p>
    </div>
  </motion.li>
);

export const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });

  return (
    <section id="processo" data-testid="process-section" className="relative py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <SectionHeading index="05" eyebrow="Como funciona" title="Da ideia ao projeto no ar, em 4 etapas." sub="Você não precisa entender de tecnologia. A WebStation conduz cada etapa com você." testid="process-title" />
            <Reveal delay={0.2}>
              <CTAButton testid="process-cta" className="mt-10" onClick={() => goToContact()}>
                Começar meu projeto
              </CTAButton>
            </Reveal>
          </div>
        </div>
        <div className="relative lg:col-span-7" ref={ref}>
          <div className="absolute bottom-0 left-7 top-0 w-px bg-white/10 sm:left-8" />
          <motion.div style={{ scaleY }} className="absolute bottom-0 left-7 top-0 w-px origin-top bg-ws-lime shadow-[0_0_12px_rgba(124,255,26,.8)] sm:left-8" />
          <ol className="relative">
            {STEPS.map((s, i) => (
              <Step key={s.title} s={s} i={i} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
